import React, { useContext, useEffect, useState } from "react";
import HomeOutlinedIcon from "@mui/icons-material/HomeOutlined";
import DashboardInfoItem from "./dashboard/DashboardInfoItem";
import { CustomContainer, FlexContainer } from "./styled/Container.styled";
import { Box, Typography, useTheme, useMediaQuery } from "@mui/material";
import { PillButton } from "./styled/Button.styled";
import { AuthContext } from "../../contexts/AuthContext";
import { format, isBefore, parseISO, startOfDay } from "date-fns";
import { NOT_APPLICABLE } from "../constants/Defaults.constants";
import { useNavigate } from "react-router-dom";
import { DOCUMENTS_ALLOW_REQUEST_PAYOFF_KEY } from "../constants/Configurator.constants";
import { ConfiguratorContext } from "../../contexts/ConfiguratorContext";
import { findInArray, isAllowed } from "../utils/utility-functions";
import { isLoanClosedToPayments } from "../utils/payment-functions";
import { utcToZonedTime } from "date-fns-tz";
import { formatCurrency } from "../utils/currency";

function LoanDetailsExpandable({
  position = "absolute",
  expanded = false,
  popup = false,
}) {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const { activeLoanDetails } = useContext(AuthContext);
  const { edittedFields } = useContext(ConfiguratorContext);

  const [isExpanded, setIsExpanded] = useState(expanded);
  const [userAddress, setUserAddress] = useState(NOT_APPLICABLE);
  const [isSpecialLoan, setIsSpecialLoan] = useState(false);

  const sumFormatter = new Intl.NumberFormat("en-US");
  const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  // Server-resolved rule (US-1062443) with raw-indicator fallback for mocks.
  const inActiveOrDeadLoan = isLoanClosedToPayments(activeLoanDetails?.loanDetails)

  const configuratorPayoffQuoteAllowField = findInArray(
    edittedFields,
    "key",
    DOCUMENTS_ALLOW_REQUEST_PAYOFF_KEY,
  );

  const canPayoffQuote = isAllowed(
    configuratorPayoffQuoteAllowField,
    "value",
    "true",
    true,
  );

  useEffect(() => {
    if (activeLoanDetails) {
      const addr = [
        activeLoanDetails?.borrowerDetails?.address?.trim(),
        activeLoanDetails?.borrowerDetails?.city?.trim(),
        activeLoanDetails?.borrowerDetails?.state?.trim(),
        activeLoanDetails?.borrowerDetails?.postalCode?.trim(),
      ];

      if (
        activeLoanDetails?.loanDetails?.loanType === "A" ||
        activeLoanDetails?.loanDetails?.amortizationType === "H" ||
        activeLoanDetails?.loanDetails?.amortizationType === "C"
      ) {
        setIsSpecialLoan(true);
      } else {
        setIsSpecialLoan(false);
      }

      const filteredAddress = addr.filter((item) => !!item);
      setUserAddress(filteredAddress.join(", "));
    }
  }, [activeLoanDetails]);

  if (!activeLoanDetails) {
    return null;
  }

  const armIndexRaw = activeLoanDetails?.loanDetails?.armIndex;
  const armIndexDisplay =
    armIndexRaw !== undefined &&
    armIndexRaw !== null &&
    String(armIndexRaw).trim() !== ""
      ? String(armIndexRaw).trim()
      : NOT_APPLICABLE;

  const loanDetails = activeLoanDetails?.loanDetails;

  const hasNumericValue = (value) =>
    value !== undefined && value !== null;

  const formatCurrencyOrNA = (value) =>
    hasNumericValue(value) ? formatCurrency(value) : NOT_APPLICABLE;

  const formatPercentOrNA = (value) =>
    hasNumericValue(value)
      ? `${sumFormatter.format(value)}%`
      : NOT_APPLICABLE;

  const helocLineOfCreditDisplay = formatCurrencyOrNA(loanDetails?.originalBalance);

  const getHelocAvailableCreditDisplay = () => {
    const creditExpDt = loanDetails?.helocCreditExpDt;
    if (!creditExpDt) {
      return formatCurrency(0);
    }

    const today = startOfDay(new Date());
    const expirationDate = startOfDay(parseISO(creditExpDt));
    if (!isBefore(today, expirationDate)) {
      return formatCurrency(0);
    }

    const lineOfCredit = loanDetails?.originalBalance;
    const mortgageBalance = loanDetails?.mortgageBalance;
    if (!hasNumericValue(lineOfCredit) || !hasNumericValue(mortgageBalance)) {
      return formatCurrency(0);
    }

    const availableCredit = Math.max(
      0,
      Number(lineOfCredit) - Number(mortgageBalance),
    );
    return formatCurrency(availableCredit);
  };

  return (
    <CustomContainer
      position={isMobile ? "relative" : position}
      sx={{
        top: isMobile ? "auto" : "25%",
        ...(popup ? {} : {
          right: {
            xs: 0,
            md: 24,
            lg: 100,
          },
        }),
        zIndex: 5,
        transition: "all 1s ease-in-out",
        width: isMobile ? "100%" : "auto",
        marginBottom: isMobile ? "20px" : 0,
        height: 'max-content',
        // display: {xs: "none", lg: "flex"},
      }}
    >
      <FlexContainer
        background={theme.palette.background.paper}
        padding={isMobile ? "15px" : "10px 15px"}
        flexdirection="column"
        alignitems="flex-start"
        justifycontent="center"
        sx={{
          flexWrap: 'wrap',
          maxWidth: isMobile ? "100%" : 924,
          height: isExpanded ? "auto" : (isMobile ? "auto" : "84px"),
          width: isMobile ? "100%" : (isExpanded ? "768px" : "514px"),
          transition: "all 0.3s ease-in-out",
          borderRadius: isMobile ? "6px" : "4px",
          ...(!popup && {
            border: `1px solid ${theme.palette.divider}`,
            boxShadow: "0 4px 12px rgba(0, 0, 0, 0.12)",
          }),
        }}
      >
        <FlexContainer 
          gap={isMobile ? "15px" : "25px"} 
          flexwrap="wrap" 
          justifycontent="flex-start"
          flexdirection={isMobile ? "column" : "row"}
          width={isMobile ? "100%" : "auto"}
          alignitems="flex-start"
          sx={{
            paddingRight: isExpanded ? "0" : "32px"
          }}
        >
          <FlexContainer gap="5px" width={isMobile ? "100%" : "auto"}>
            <HomeOutlinedIcon color="primary" fontSize={isMobile ? "medium" : "large"} />
            {isExpanded && (
              <Typography width="100%" color="primary" fontWeight="bold" fontSize={isMobile ? "1rem" : "inherit"}>
                Loan Details
              </Typography>
            )}
          </FlexContainer>
          <FlexContainer
            gap={isMobile ? "15px" : "25px"}
            sx={{
              width: isExpanded ? "100%" : "auto",
              transition: "all 0.3s ease-in-out",
              flexDirection: "row",
              flexWrap: "wrap",
              alignItems: 'flex-start',
              '& > div': {
                flexGrow: 1,
                flexShrink: 1,
                flexBasis: {
                  xs: '120px',
                  md: 'unset'
                },
                maxWidth: '100%',
                '& > div': {
                  '& > p': {
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word'
                  }
                }
              },
            }}
            justifycontent="flex-start"
          >
            <DashboardInfoItem
              label="Current Balance"
              labelFontSize={isMobile ? 12 : 10}
              data={formatCurrencyOrNA(loanDetails?.mortgageBalance)}
              valueFontSize={isMobile ? 16 : 14}
              textOverflow
              withTooltip
              maxWidth={isMobile ? "100%" : "90px"}
            />
            <DashboardInfoItem
              label="Monthly Payment"
              labelFontSize={isMobile ? 12 : 10}
              data={formatCurrencyOrNA(loanDetails?.amountDue)}
              valueFontSize={isMobile ? 16 : 14}
              textOverflow
              withTooltip
              maxWidth={isMobile ? "100%" : "90px"}
            />
            <DashboardInfoItem
              label="Interest Rate"
              labelFontSize={isMobile ? 12 : 10}
              data={formatPercentOrNA(loanDetails?.interestRate)}
              valueFontSize={isMobile ? 16 : 14}
              textOverflow
              withTooltip
              maxWidth={isMobile ? "100%" : "90px"}
            />
            {hasNumericValue(loanDetails?.escrowBalance) ? (
              <DashboardInfoItem
                label="Escrow Balance"
                labelFontSize={isMobile ? 12 : 10}
                data={formatCurrency(loanDetails.escrowBalance)}
                valueFontSize={isMobile ? 16 : 14}
                textOverflow
                withTooltip
                maxWidth={isMobile ? "100%" : "90px"}
              />
            ) : null}
            {isExpanded && (
              <>
                <DashboardInfoItem
                  label="Original Loan Balance"
                  labelFontSize={isMobile ? 12 : 10}
                  data={formatCurrencyOrNA(loanDetails?.originalBalance)}
                  valueFontSize={isMobile ? 16 : 14}
                  textOverflow
                  withTooltip
                  maxWidth={isMobile ? "100%" : "90px"}
                />
                {!inActiveOrDeadLoan && canPayoffQuote && (
                  <Box
                    sx={{
                      lineHeight: 1.286,
                      width: "100%",
                      minWidth: "100%",
                    }}
                  >
                    <PillButton
                      variant="outlined"
                      customfontweight="bold"
                      onClick={(e) =>
                        navigate("/statements-documents/request", {
                          replace: true,
                        })
                      }
                      sx={{
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        display: "block",
                        fontSize: isMobile ? "14px" : "inherit",
                        padding: isMobile ? "12px 16px" : "inherit",
                        width: {
                          xs: '100%',
                        }
                      }}
                    >
                      Request Payoff Quote
                    </PillButton>
                  </Box>
                )}
              </>
            )}
          </FlexContainer>
        </FlexContainer>
        <FlexContainer
          gap={isMobile ? "15px" : "25px"}
          sx={{
            height: isExpanded ? "auto" : 0,
            paddingTop: isExpanded ? (isMobile ? "15px" : "25px") : 0,
            overflow: "hidden",
            transition: "all 0.3s ease-in-out",
            flexDirection: "row",
            flexWrap: "wrap",
            width: "100%",
            alignItems: 'flex-start',
            '& > div': {
              flex: '1 1 120px',
              maxWidth: '100%',
              '& > div': {
                '& > p': {
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word'
                }
              }
            },
          }}
        >
          <DashboardInfoItem
            label="Property Address"
            labelFontSize={isMobile ? 12 : 10}
            data={userAddress || NOT_APPLICABLE}
            valueFontSize={isMobile ? 16 : 14}
            textOverflow
            withTooltip
            maxWidth={isMobile ? "100%" : "393px"}
          />
          {hasNumericValue(loanDetails?.amountInterestLastYear) ? (
            <DashboardInfoItem
              label="Interest Paid Last Year"
              labelFontSize={isMobile ? 12 : 10}
              data={formatCurrency(loanDetails.amountInterestLastYear)}
              valueFontSize={isMobile ? 16 : 14}
              textOverflow
              withTooltip
              maxWidth={isMobile ? "100%" : "125px"}
            />
          ) : null}
          <DashboardInfoItem
            label="Maturity Date"
            labelFontSize={isMobile ? 12 : 10}
            data={
              activeLoanDetails?.loanDetails?.maturityDate
                ? format(
                    utcToZonedTime(
                      activeLoanDetails?.loanDetails?.maturityDate,
                      userTimeZone,
                    ),
                    "MMM d, yyyy",
                  )
                : NOT_APPLICABLE
            }
            valueFontSize={isMobile ? 16 : 14}
            textOverflow
            withTooltip
            maxWidth={isMobile ? "100%" : "125px"}
          />
          <DashboardInfoItem
            label="Closing Date"
            labelFontSize={isMobile ? 12 : 10}
            data={
              activeLoanDetails?.loanDetails?.closingDate
                ? format(
                    utcToZonedTime(
                      activeLoanDetails?.loanDetails?.closingDate,
                      userTimeZone,
                    ),
                    "MMM d, yyyy",
                  )
                : NOT_APPLICABLE
            }
            valueFontSize={isMobile ? 16 : 14}
            textOverflow
            withTooltip
            maxWidth={isMobile ? "100%" : "125px"}
          />
        </FlexContainer>
        {isSpecialLoan && (
          <FlexContainer
            gap={isMobile ? "15px" : "25px"}
            sx={{
              height: isExpanded ? "auto" : 0,
              paddingTop: isExpanded ? (isMobile ? "15px" : "25px") : 0,
              overflow: "hidden",
              transition: "all 0.3s ease-in-out",
              flexDirection: "row",
              flexWrap: "wrap",
              width: "100%",
              alignItems: 'flex-start',
              '& > div': {
                flex: '1 1 120px',
                maxWidth: '100%',
                '& > div': {
                  '& > p': {
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word'
                  }
                }
              },
            }}
          >
            {activeLoanDetails?.loanDetails?.loanType === "A" && (
              <>
                <DashboardInfoItem
                  label="Index"
                  labelFontSize={isMobile ? 12 : 10}
                  data={armIndexDisplay}
                  valueFontSize={isMobile ? 16 : 14}
                  textOverflow
                  withTooltip
                  maxWidth={isMobile ? "100%" : "125px"}
                />
                <DashboardInfoItem
                  label="Rate Next Change Date"
                  labelFontSize={isMobile ? 12 : 10}
                  data={
                    activeLoanDetails?.loanDetails?.armRateNextChangeDate
                      ? format(
                          utcToZonedTime(
                            activeLoanDetails?.loanDetails
                              ?.armRateNextChangeDate,
                            userTimeZone,
                          ),
                          "MMM d, yyyy",
                        )
                      : NOT_APPLICABLE
                  }
                  valueFontSize={isMobile ? 16 : 14}
                  textOverflow
                  withTooltip
                  maxWidth={isMobile ? "100%" : "125px"}
                />
                <DashboardInfoItem
                  label="Payment Next Change Date"
                  labelFontSize={isMobile ? 12 : 10}
                  data={
                    activeLoanDetails?.loanDetails?.armPaymentNextChangeDate
                      ? format(
                          utcToZonedTime(
                            activeLoanDetails?.loanDetails
                              ?.armPaymentNextChangeDate,
                            userTimeZone,
                          ),
                          "MMM d, yyyy",
                        )
                      : NOT_APPLICABLE
                  }
                  valueFontSize={isMobile ? 16 : 14}
                  textOverflow
                  withTooltip
                  maxWidth={isMobile ? "100%" : "125px"}
                />
              </>
            )}
            {activeLoanDetails?.loanDetails?.amortizationType === "H" && (
              <>
                <DashboardInfoItem
                  label="Line of Credit"
                  labelFontSize={isMobile ? 12 : 10}
                  data={helocLineOfCreditDisplay}
                  valueFontSize={isMobile ? 16 : 14}
                  textOverflow
                  withTooltip
                  maxWidth={isMobile ? "100%" : "125px"}
                />
                <Box sx={{ marginLeft: { xs: 0, md: "-250px" } }}>
                  <DashboardInfoItem
                    label="Available Credit"
                    labelFontSize={isMobile ? 12 : 10}
                    data={getHelocAvailableCreditDisplay()}
                    valueFontSize={isMobile ? 16 : 14}
                    textOverflow
                    withTooltip
                    maxWidth={isMobile ? "100%" : "125px"}
                  />
                </Box>
              </>
            )}
            {activeLoanDetails?.loanDetails?.amortizationType === "C" && (
              <DashboardInfoItem
                label="Construction Balance"
                labelFontSize={isMobile ? 12 : 10}
                data={formatCurrencyOrNA(loanDetails?.hcAvailable)}
                valueFontSize={isMobile ? 16 : 14}
                textOverflow
                withTooltip
                maxWidth={isMobile ? "100%" : "125px"}
              />
            )}
          </FlexContainer>
        )}
      </FlexContainer>
      {!expanded && (
        <Typography
          fontSize={isMobile ? 14 : 10}
          fontWeight="bold"
          color="primary"
          sx={{
            position: isMobile ? "static" : "absolute",
            top: isMobile ? "auto" : 30,
            right: isMobile ? "auto" : 15,
            cursor: "pointer",
            userSelect: "none",
            textAlign: isMobile ? "center" : "left",
            marginTop: isMobile ? "15px" : 0,
            padding: isMobile ? "10px" : 0,
            background: isMobile ? "rgba(255, 232, 186, 0.8)" : "transparent",
            borderRadius: isMobile ? "8px" : 0,
            border: isMobile ? "1px solid #ffd54f" : "none",
          }}
          onClick={(e) =>
            setIsExpanded((previousIsExpanded) => !previousIsExpanded)
          }
        >
          {isExpanded ? "See Less" : "View More"}
        </Typography>
      )}
    </CustomContainer>
  );
}

export default LoanDetailsExpandable;